import { useCallback } from "react";

import { toast } from "@performyard/py-components";
import type { BulkUpdateReviewCycleReportsRequestInput } from "@performyard/service-contracts";

import { useBulkDeleteReviewCycleReports, useBulkUpdateReviewCycleReports } from "../../../../../api/hooks";

import { formCountLabel } from "../bulk-actions";

type BulkUpdates = BulkUpdateReviewCycleReportsRequestInput["updates"];

/**
 * Applies a bulk action to the selected forms. Both endpoints are best-effort, so a partial success
 * is normal and the skipped count gets its own warning toast.
 *
 * Errors are left to the query client's global mutation handler, which already toasts the API
 * message (falling back to the hook's `meta.errorMessage`) — a second toast here would double up.
 */
export const useReviewFormsBulkActions = ({
	reviewCycleId,
	selectedIds,
	clearSelection,
	closeModal,
}: {
	reviewCycleId: string;
	selectedIds: Set<string>;
	clearSelection: () => void;
	closeModal: () => void;
}) => {
	// Count comes from the API, not the selection: it includes reports whose value already matched
	// (per the AC) but excludes any the API skipped.
	const reportOutcome = useCallback(
		(verb: "updated" | "deleted", affectedCount: number, warningCount: number) => {
			if (affectedCount > 0) {
				toast.success(`${formCountLabel(affectedCount)} ${verb}`);
			}
			if (warningCount > 0) {
				const wasOrWere = warningCount === 1 ? "was" : "were";
				toast.warning(`${formCountLabel(warningCount)} couldn't be ${verb} and ${wasOrWere} skipped`);
			}

			// Nothing changed, so keep the selection for another attempt instead of clearing it
			if (affectedCount > 0) clearSelection();
			closeModal();
		},
		[clearSelection, closeModal]
	);

	const { mutate: bulkUpdate, isPending: isUpdating } = useBulkUpdateReviewCycleReports({
		onSuccess: (response) =>
			reportOutcome("updated", response.data.updatedReportIds.length, response.data.warnings.length),
	});

	const { mutate: bulkDelete, isPending: isDeleting } = useBulkDeleteReviewCycleReports({
		onSuccess: (response) =>
			reportOutcome("deleted", response.data.deletedReportIds.length, response.data.warnings.length),
	});

	const applyUpdates = useCallback(
		(updates: BulkUpdates) => bulkUpdate({ reviewCycleId, reviewReportIds: [...selectedIds], updates }),
		[bulkUpdate, reviewCycleId, selectedIds]
	);

	const confirmDelete = useCallback(
		() => bulkDelete({ reviewCycleId, reviewReportIds: [...selectedIds] }),
		[bulkDelete, reviewCycleId, selectedIds]
	);

	return { applyUpdates, confirmDelete, isSaving: isUpdating || isDeleting };
};
